import { useContext, useMemo, useState, useEffect } from 'react'
import classNames from 'classnames/bind'
import moment from 'moment-timezone'
import { useTranslation } from 'react-i18next'
import { isEmpty, values, map, pipe, sort, prop, descend, filter } from 'ramda'

import EmptyWarn from 'app/common/layouts/EmptyWarn'
import Loading from 'app/common/components/Loader'
import toFixedValue from 'app/common/lib/toFixedValue'
import { BannerContent } from 'app/common/components-next/banner'
import { Table, Tr, Td, Th, useCopyColumnsWidths } from 'app/common/ui-components/table'
import { useCurrencySign } from 'app/common/components-next/currency'
import Sticky from 'app/common/templates-next/sticky'
import { PageTemplateContent } from 'app/common/templates-next/page-template'
import PlainTooltip from 'app/common/ui-next/plain-tooltip'

import Individual from '../commonComponents/Individual'
import CreatedWith from '../commonComponents/CreatedWith'
import StatusTip from '../commonComponents/StatusTip'
import { Purchases, statuses } from '../PurchasesApi'
import ComplexTooltip from './ComplexTooltip'
import styles from './OrdersTable.module.scss';

const cx = classNames.bind(styles)

type Props = {
  timezone: string
  statusFilter?: (typeof statuses)[keyof typeof statuses] | null
}

const OrdersTable = ({ timezone, statusFilter }: Props) => {
  if (!timezone) {
    console.error("OrdersTable — не пришел обязательный параметр 'timezone'") // eslint-disable-line no-console
  }
  const { t } = useTranslation('cafeteria')
  const currency = useCurrencySign('commaSpaceBefore')

  const {
    content: { purchases },
    loading,
  } = useContext(Purchases)
  const tableHeaders = t('orders_table_columns', { returnObjects: true })

  const orderItems = useMemo(
    () =>
      pipe(
        values,
        (val) =>
          statusFilter || statusFilter === 0
            ? filter(({ status }: any) => status === statusFilter, val)
            : val,
        map(
          ({
            guid,
            datetime,
            individual,
            items,
            created_by: createdBy,
            conducted_by: conductedBy,
            created_with: createdWith,
            status,
            total,
            number,
          }: any) => {
            const mDate = moment(datetime).tz(timezone)
            const date = mDate.format('DD.MM.YYYY')
            const time = mDate.format('HH:mm')
            const itemsArray = values(items)
            return {
              guid,
              seconds: mDate.unix(),
              number, // номер заказа
              status,
              createdBy,
              conductedBy,
              individual,
              createdWith,
              date,
              time,
              total: toFixedValue(total),
              items: map(
                ({ id, title, n, cost: itemCost, type, items: complexItems }) => ({
                  id,
                  title,
                  cost: toFixedValue(itemCost),
                  itemsNumber: n, // количество
                  type,
                  complexItems,
                }),
                itemsArray,
              ),
            }
          },
        ),
        sort(descend(prop('seconds'))),
      )(purchases),
    [purchases, timezone, statusFilter],
  )

  const empty = isEmpty(purchases)

  const [needUpdate, setNeedUpdate] = useState(false)
  useEffect(() => {
    setNeedUpdate(true)
  }, [statusFilter])
  useEffect(() => {
    setNeedUpdate(false)
  }, [needUpdate])

  const [refFrom, refTo] = useCopyColumnsWidths()

  return (
    <>
      <Sticky>
        {(ref, sticky, shadow) => (
          <PageTemplateContent
            className={cx({ sticky })}
            shadowBottom={shadow}
            reference={ref}
            skipPadding
            flex
          >
            <div className={styles.tableContainer}>
              <Table className={styles.table}>
                <thead>
                  <Tr header reference={refTo} headerHasBorderBottom={false}>
                    <Th alignLeft id="header1" className={styles.th}>
                      {tableHeaders[0]}
                    </Th>
                    <Th alignLeft id="header2" className={styles.th}>
                      {tableHeaders[1]}
                    </Th>
                    <Th alignLeft id="header3" className={styles.th}>
                      {tableHeaders[2]}
                    </Th>
                    <Th alignLeft id="header4" className={styles.th}>
                      {tableHeaders[3]}
                    </Th>
                    <Th alignRight id="header5" className={styles.th}>
                      {tableHeaders[4]}
                      {currency}
                    </Th>
                    <Th alignRight id="header6" className={styles.th}>
                      {tableHeaders[5]}
                    </Th>
                    <Th alignRight id="header7" className={styles.th}>
                      {tableHeaders[6]}
                      {currency}
                    </Th>
                  </Tr>
                </thead>
              </Table>
            </div>

            <BannerContent
              placeId="d099e4c6-f44b-42ef-b5f9-a1d52c27dee5"
              className={styles.bannerPlaceholder}
              displayAsPlaceholder
              height={0}
            />
          </PageTemplateContent>
        )}
      </Sticky>

      <PageTemplateContent className={styles.layout} skipPadding flex>
        <div className={styles.tableContainer}>
          {loading && <Loading />}

          {!loading && empty && (
            <EmptyWarn>{t('orders_table_empty', 'Нет заказов за выбранный период')}</EmptyWarn>
          )}

          {!loading && (
            <Table className={styles.table}>
              {!needUpdate && (
                <thead>
                  <Tr header hidden reference={refFrom}>
                    <Th alignLeft className={styles.th}>
                      {tableHeaders[0]}
                    </Th>
                    <Th alignLeft className={styles.th}>
                      {tableHeaders[1]}
                    </Th>
                    <Th alignLeft className={styles.th}>
                      {tableHeaders[2]}
                    </Th>
                    <Th alignLeft className={styles.th}>
                      {tableHeaders[3]}
                    </Th>
                    <Th alignRight className={styles.th}>
                      {tableHeaders[4]}
                      {currency}
                    </Th>
                    <Th alignRight className={styles.th}>
                      {tableHeaders[5]}
                    </Th>
                    <Th alignRight className={styles.th}>
                      {tableHeaders[6]}
                      {currency}
                    </Th>
                  </Tr>
                </thead>
              )}
              <tbody>
                {orderItems.map(
                  (
                    {
                      guid,
                      status,
                      createdBy,
                      conductedBy,
                      individual,
                      createdWith,
                      number,
                      date,
                      time,
                      items,
                      total,
                    },
                    i,
                  ) => {
                    const rowClassName = cx(styles.row, {
                      refWait: status === 0, // wait
                      refActive: status === 1, // ok
                      refDeleted: status === -1, // cancelled
                    })

                    return (
                      <Tr key={guid || i} className={rowClassName}>
                        <Td headers="header1">
                          <div className={styles.iconsAligned}>
                            <StatusTip
                              status={status}
                              createdBy={createdBy}
                              conductedBy={conductedBy}
                            />
                            <Individual individual={individual} />
                            <CreatedWith createdWith={createdWith} />
                          </div>
                        </Td>

                        <Td headers="header2">{number}</Td>

                        <Td headers="header3">
                          {date}
                          <br />
                          {time}
                        </Td>

                        <Td headers="header4" className={styles.itemsCell}>
                          <ul className={styles.list}>
                            {items.map(({ id, title, type, complexItems }) =>
                              type === 1 ? (
                                <li key={id} className={styles.complexTitle}>
                                  <ComplexTooltip items={complexItems} title={title}>
                                    {title}
                                  </ComplexTooltip>
                                </li>
                              ) : (
                                <li key={id} className={styles.dishTitle}>
                                  <PlainTooltip text={title}>{title}</PlainTooltip>
                                </li>
                              ),
                            )}
                          </ul>
                        </Td>

                        <Td alignRight headers="header5">
                          <ul className={styles.list}>
                            {items.map(({ id, cost }) => (
                              <li key={id}>{cost}</li>
                            ))}
                          </ul>
                        </Td>

                        <Td alignRight headers="header6">
                          <ul className={styles.list}>
                            {items.map(({ id, itemsNumber }) => (
                              <li key={id}>{itemsNumber}</li>
                            ))}
                          </ul>
                        </Td>

                        <Td alignRight headers="header7" className={styles.totalCell}>
                          {total}
                        </Td>
                      </Tr>
                    )
                  },
                )}
              </tbody>
            </Table>
          )}
        </div>

        <BannerContent
          // leaveContainerOnEmpty
          refreshOnMount
          className={styles.bannerContent}
          placeId="d099e4c6-f44b-42ef-b5f9-a1d52c27dee5"
        />
      </PageTemplateContent>
    </>
  )
}

export default OrdersTable
